import { useState, useId } from "react";

/* ---------- Star (PRECISE decimal fill) ---------- */
const Star = ({ fill = 0, className = "" }) => {
  const uid = useId();
  const gradId = `starGrad-${uid}`;

  return (
    <svg viewBox="0 0 24 24" className={`w-5 h-5 ${className}`}>
      <defs>
        <linearGradient id={gradId} x1="0" x2="1">
          <stop offset={`${fill}%`} stopColor="currentColor" />
          <stop offset={`${fill}%`} stopColor="transparent" />
        </linearGradient>
      </defs>

      {/* Filled portion */}
      <path
        fill={`url(#${gradId})`}
        d="M12 .587l3.668 7.43 8.2 1.192-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.868 1.402-8.168L.132 9.209l8.2-1.192z"
      />

      {/* Empty star base */}
      <path
        fill="currentColor"
        className="opacity-30"
        d="M12 .587l3.668 7.43 8.2 1.192-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.868 1.402-8.168L.132 9.209l8.2-1.192z"
      />
    </svg>
  );
};

/* ---------- Avatar (with initials fallback) ---------- */
const Avatar = ({ src, alt, name }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={alt || name}
        loading="lazy"
        className="w-12 h-12 rounded-full object-cover ring-1 ring-gray-200"
      />
    );
  }

  const initials = (name || "?")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="w-12 h-12 rounded-full bg-gray-200 text-gray-700 grid place-items-center font-semibold ring-1 ring-gray-200">
      {initials}
    </div>
  );
};

/* ---------- Testimonial Card ---------- */
const TestimonialCard = ({ testimonial }) => {
  const {
    id,
    name,
    role,
    avatar,
    comment = "",
    rating = 5,
  } = testimonial || {};

  const [expanded, setExpanded] = useState(false);

  /* ⭐ PRECISE STAR FILL — NO ROUNDING */
  const stars = Array.from({ length: 5 }, (_, i) => {
    return Math.max(0, Math.min(1, rating - i)) * 100;
  });

  const showToggle = comment.length > 80;
  const commentId = `t-comment-${id ?? name ?? Math.random()}`;

  return (
    <div className="bg-white p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="flex items-center mb-3">
        <div className="mr-4">
          <Avatar src={avatar} name={name} />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900">{name}</h3>
          {role && <p className="text-gray-600 text-sm">{role}</p>}
        </div>
      </div>

      {/* Stars */}
      <div
        className="flex items-center gap-1 mb-4"
        aria-label={`Rating: ${rating} out of 5`}
      >
        {stars.map((fill, i) => (
          <Star key={i} fill={fill} className="text-yellow-400" />
        ))}
        <span className="ml-2 text-xs text-gray-500">
          {rating.toFixed(1)} / 5
        </span>
      </div>

      {/* Comment */}
      {comment && (
        <>
          <div
            id={commentId}
            className={`overflow-hidden transition-[max-height] duration-500 ${
              expanded ? "max-h-[800px]" : "max-h-16"
            }`}
          >
            <p
              className={`italic text-gray-700 leading-6 ${
                expanded ? "" : "line-clamp-2"
              }`}
            >
              “{comment}”
            </p>
          </div>

          {showToggle && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 text-sm font-medium text-primary-600 hover:underline flex items-center gap-1"
              aria-expanded={expanded}
              aria-controls={commentId}
            >
              {expanded ? "Show less" : "Read full review"}
              <svg
                className={`w-4 h-4 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default TestimonialCard;
